import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Check, Sunrise, Command, Flame, Sparkles, RefreshCw, Quote } from "lucide-react";
import { Input } from "@/components/ui/input";

const PRIORITY_DOT = { urgent: "bg-red-400", important: "bg-[#D4AF37]", medium: "bg-[#00E5FF]", low: "bg-zinc-500" };

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good arvo";
  return "Good evening";
};

export const MorningBriefing = ({ tasks }) => {
  const [name, setName] = useState("");
  const [osLines, setOsLines] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [draft, setDraft] = useState("");
  const [focus, setFocus] = useState(null);
  const [focusLoading, setFocusLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const loadQuote = useCallback(async (refresh = false) => {
    setQuoteLoading(true);
    try {
      const { data } = await api.get(`/briefing/quote${refresh ? "?refresh=1" : ""}`);
      setQuote(data);
    } catch (err) {
      console.error("Daily quote load failed:", err); // section stays quiet
    } finally {
      setQuoteLoading(false);
    }
  }, []);

  const loadFocus = useCallback(async (refresh = false) => {
    setFocusLoading(true);
    try {
      const { data } = await api.get(`/briefing/ai-focus${refresh ? "?refresh=1" : ""}`);
      setFocus(data);
    } catch (err) {
      console.error("AI focus load failed:", err); // falls back to tasks prop
    } finally {
      setFocusLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/profile-name").then((r) => setName(r.data.name)).catch((err) => console.error("Profile name load failed:", err));
    api.get("/operating-system").then((r) => setOsLines(r.data.lines)).catch((err) => console.error("OS lines load failed:", err));
    loadFocus();
    loadQuote();
  }, [loadFocus, loadQuote]);

  const saveName = async () => {
    try {
      const { data } = await api.put("/profile-name", { name: draft });
      setName(data.name);
      setEditingName(false);
      toast.success("Name saved");
    } catch { toast.error("Couldn't save name"); }
  };

  const today = new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });
  const focusTasks = focus?.tasks?.length ? focus.tasks : tasks.slice(0, 4);
  const isAi = Boolean(focus?.ai);

  return (
    <section className="max-w-7xl mx-auto px-6 pt-10" data-testid="briefing-section">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="border border-[#D4AF37]/25 rounded-md bg-gradient-to-br from-[#141310] to-[#0F0F11] p-6 sm:p-8"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
          <div className="flex items-baseline gap-3">
            <Sunrise className="w-5 h-5 text-[#D4AF37] self-center" />
            {editingName ? (
              <span className="inline-flex items-center gap-2">
                <h2 className="font-display text-2xl sm:text-3xl text-white" data-testid="briefing-greeting">{greeting()},</h2>
                <Input
                  value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
                  className="bg-white/5 border-white/10 text-white rounded-sm h-9 w-40 text-lg focus-visible:ring-[#D4AF37]"
                  data-testid="briefing-name-input"
                />
                <button onClick={saveName} className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200" data-testid="briefing-name-save">
                  <Check className="w-4 h-4" />
                </button>
              </span>
            ) : (
              <span className="inline-flex items-baseline gap-2 group">
                <h2 className="font-display text-2xl sm:text-3xl text-white" data-testid="briefing-greeting">
                  {greeting()}, <span className="text-[#D4AF37]">{name}</span>.
                </h2>
                <button
                  onClick={() => { setEditingName(true); setDraft(name); }}
                  className="text-zinc-600 hover:text-[#D4AF37] transition-colors duration-200"
                  data-testid="briefing-name-edit" title="Edit your name"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500" data-testid="briefing-date">{today}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div data-testid="briefing-quote">
            <p className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 flex items-center gap-2">
              <Quote className="w-3 h-3" /> Daily wisdom
              <button
                onClick={() => loadQuote(true)} disabled={quoteLoading}
                className="ml-auto text-zinc-600 hover:text-[#D4AF37] transition-colors duration-200 disabled:opacity-50"
                title="New quote" data-testid="briefing-quote-refresh"
              >
                <RefreshCw className={`w-3 h-3 ${quoteLoading ? "animate-spin" : ""}`} />
              </button>
            </p>
            {quoteLoading && !quote ? (
              <p className="text-xs text-zinc-600 italic" data-testid="briefing-quote-loading">Consulting the greats…</p>
            ) : quote ? (
              <blockquote className="border-l-2 border-[#D4AF37]/40 pl-3">
                <p className="text-sm text-zinc-200 leading-relaxed italic" data-testid="briefing-quote-text">"{quote.text}"</p>
                <footer className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-2" data-testid="briefing-quote-author">— {quote.author}</footer>
              </blockquote>
            ) : (
              <p className="text-xs text-zinc-600 italic" data-testid="briefing-quote-empty">Wisdom arrives with the sunrise.</p>
            )}
          </div>

          <div data-testid="briefing-os">
            <p className="text-[9px] uppercase tracking-[0.3em] text-[#00E5FF] mb-3 inline-flex items-center gap-2">
              <Command className="w-3 h-3" /> The code you run on
            </p>
            <ul className="space-y-1.5">
              {osLines.map((l, i) => (
                <li key={`${l}-${i}`} className="text-xs text-zinc-300 leading-relaxed">{l}</li>
              ))}
            </ul>
          </div>

          <div data-testid="briefing-focus">
            <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-400 mb-3 flex items-center gap-2">
              <Flame className="w-3 h-3" /> Today's focus
              {isAi && (
                <span className="inline-flex items-center gap-1 text-[8px] font-bold tracking-[0.15em] px-1.5 py-0.5 rounded-sm bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF]" data-testid="briefing-ai-badge">
                  <Sparkles className="w-2.5 h-2.5" /> AI
                </span>
              )}
              <button
                onClick={() => loadFocus(true)} disabled={focusLoading}
                className="ml-auto text-zinc-600 hover:text-[#00E5FF] transition-colors duration-200 disabled:opacity-50"
                title="Re-pick today's focus" data-testid="briefing-focus-refresh"
              >
                <RefreshCw className={`w-3 h-3 ${focusLoading ? "animate-spin" : ""}`} />
              </button>
            </p>
            {focusLoading && !focus ? (
              <p className="text-xs text-zinc-600 italic" data-testid="briefing-focus-loading">Picking today's plays…</p>
            ) : focusTasks.length === 0 ? (
              <p className="text-xs text-zinc-600 italic" data-testid="briefing-focus-empty">All clear. Go live your life.</p>
            ) : (
              <>
                <ul className="space-y-2">
                  {focusTasks.map((t) => (
                    <li key={t.id} className="flex items-center gap-2 text-xs text-zinc-200" data-testid={`briefing-focus-item-${t.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PRIORITY_DOT[t.priority || "medium"]}`} />
                      <span className="truncate">{t.title}</span>
                    </li>
                  ))}
                </ul>
                {focus?.note && (
                  <p className="text-[10px] text-zinc-500 italic mt-3 leading-relaxed" data-testid="briefing-focus-note">"{focus.note}"</p>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
};
